import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { commentSchema } from "@/lib/schema";
import { sendTransactionalEmail } from "@/lib/sendTransactionalEmail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { checkRateLimit } from "@/lib/rateLimit";
import { useAuth } from "@/hooks/useAuth";

interface CommentsProps {
  articleId: string;
  /** Article title passed from ArticlePage — used in the comment-received confirmation email. */
  articleTitle?: string;
  /** Article slug — used to build the article URL in the email. */
  articleSlug?: string;
}

interface PublicComment {
  id: string;
  author_name: string;
  body: string;
  created_at: string;
}

export function Comments({
  articleId,
  articleTitle,
  articleSlug,
}: CommentsProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");

  // Consistent UUID validation: same regex used throughout the codebase
  const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const isDbArticle = UUID_RE.test(articleId);

  const {
    data: comments = [],
    isLoading: loading,
    refetch: fetchComments,
  } = useQuery({
    queryKey: ["comments", articleId],
    queryFn: async () => {
      if (!isDbArticle) return [];
      const { data, error } = await supabase
        .from("comments_public")
        .select("id, author_name, body, created_at")
        .eq("article_id", articleId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as PublicComment[]) || [];
    },
    enabled: isDbArticle,
  });

  // Realtime subscription for live approved comments
  useEffect(() => {
    if (!isDbArticle) return;

    const channel = supabase
      .channel(`public-comments-${articleId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "comments",
          filter: `article_id=eq.${articleId}`,
        },
        (payload) => {
          // Only re-fetch if the new comment is approved
          // (Comments are moderated by default)
          if (payload.new.status === "approved") {
            fetchComments();
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "comments",
          filter: `article_id=eq.${articleId}`,
        },
        (payload) => {
          // If status changed to approved, re-fetch
          if (payload.new.status === "approved") {
            fetchComments();
          }
        },
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [articleId, isDbArticle, fetchComments]);

  const mutation = useMutation({
    mutationFn: async (payload: {
      article_id: string;
      author_name: string;
      author_email: string;
      body: string;
      status: string;
    }) => {
      const { error } = await supabase.from("comments").insert(payload);
      if (error) throw error;
      return payload;
    },
    onSuccess: (payload) => {
      toast.success("Comment posted!", {
        description: "Your comment is awaiting moderation.",
      });
      // Send confirmation email to the commenter (fire-and-forget — don't block UI).
      void sendTransactionalEmail({
        templateName: "comment-received",
        recipientEmail: payload.author_email,
        idempotencyKey: `comment-received-${articleId}-${payload.author_email}`,
        templateData: {
          name: payload.author_name,
          articleTitle: articleTitle ?? "this article",
          articleSlug: articleSlug ?? "",
        },
      });
      setName("");
      setEmail("");
      setBody("");
      queryClient.invalidateQueries({ queryKey: ["comments", articleId] });
    },
    onError: (err: Error) => {
      toast.error("Failed to post comment", { description: err.message });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkRateLimit("comment_submission", 5, 60000)) {
      toast.error("Too many attempts", {
        description: "Please try again later.",
      });
      return;
    }

    // Validation status handled by toast.error

    const parsed = commentSchema.safeParse({
      article_id: articleId,
      author_name: name.trim(),
      author_email: email.trim(),
      body: body.trim(),
      status: "pending",
    });

    if (!parsed.success) {
      toast.error("Invalid comment", {
        description: parsed.error.issues[0].message,
      });
      return;
    }

    mutation.mutate(parsed.data);
  };

  if (!isDbArticle) return null;

  return (
    <section className="mt-12 pt-8 border-t">
      <div className="flex items-center gap-2 mb-5">
        <MessageCircle className="h-5 w-5 text-primary" />
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-lg md:text-xl font-bold text-foreground">
            Comments{" "}
            {comments.length > 0 && (
              <span className="text-muted-foreground">({comments.length})</span>
            )}
          </h2>
          {!user && (
            <span className="text-xs md:text-sm text-destructive font-medium bg-destructive/10 px-2 py-1 rounded">
              You must be logged in to post a comment
            </span>
          )}
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border bg-card p-4 space-y-3 mb-6"
      >
        <h3 className="text-sm font-semibold text-foreground">
          Leave a comment
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="comment-name" className="sr-only">
              Your name
            </label>
            <Input
              id="comment-name"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="comment-email" className="sr-only">
              Your email address
            </label>
            <Input
              id="comment-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
        </div>
        <div>
          <label htmlFor="comment-body" className="sr-only">
            Comment
          </label>
          <Textarea
            id="comment-body"
            placeholder="Share your thoughts…"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={!user || mutation.isPending || !name || !email || !body}
            title={!user ? "Please log in to post a comment" : ""}
          >
            {!user ? "Login to Post" : mutation.isPending ? "Posting…" : "Post Comment"}
          </Button>
        </div>
      </form>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading comments…</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Be the first to comment.
        </p>
      ) : (
        <ul className="space-y-4">
          {comments.map((c) => (
            <li key={c.id} className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                  {c.author_name.slice(0, 2).toUpperCase()}
                </div>
                <span className="text-sm font-semibold text-foreground">
                  {c.author_name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {new Date(c.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {c.body}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
